import React, { useState, useEffect } from 'react';
import API_URL, { getAuthHeaders } from '../utils/api';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

function CatalogoMateriales() {
  const [materiales, setMateriales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingPrice, setEditingPrice] = useState({});
  const [customPrices, setCustomPrices] = useState({});
  const [newMaterial, setNewMaterial] = useState({
    codigo: '',
    descripcion: '',
    unidad_id: 1,
    precio_base: '',
    proveedor: ''
  });
  const [unidades, setUnidades] = useState([]);

  useEffect(() => {
    fetchMateriales();
    fetchUnidades();
    loadCustomPrices();
  }, [search]);

  const fetchMateriales = async () => {
    try {
      setLoading(true);
      const url = search
        ? `${API_URL}/materiales?search=${encodeURIComponent(search)}`
        : `${API_URL}/materiales`;

      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Error al cargar materiales');

      const data = await response.json();
      setMateriales(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnidades = async () => {
    try {
      const response = await fetch(`${API_URL}/unidades`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setUnidades(data);
      }
    } catch (err) {
      console.error('Error cargando unidades:', err);
    }
  };

  const loadCustomPrices = () => {
    const savedPrices = localStorage.getItem('customMaterialPrices');
    if (savedPrices) {
      setCustomPrices(JSON.parse(savedPrices));
    }
  };

  const formatPrice = (price) => {
    return parseFloat(price).toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    });
  };

  const handleEditPrice = (materialId, currentPrice) => {
    setEditingId(materialId);
    setEditingPrice({ [materialId]: currentPrice });
  };

  const handleSavePrice = async (materialId) => {
    const newPrice = editingPrice[materialId];

    // Guardar precio personalizado en localStorage
    const updatedCustomPrices = {
      ...customPrices,
      [materialId]: newPrice
    };
    setCustomPrices(updatedCustomPrices);
    localStorage.setItem('customMaterialPrices', JSON.stringify(updatedCustomPrices));

    // Actualizar en la base de datos
    try {
      const response = await fetch(`${API_URL}/materiales/${materialId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ precio_actualizado: newPrice })
      });

      if (response.ok) {
        fetchMateriales();
      }
    } catch (err) {
      console.error('Error actualizando precio:', err);
    }

    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingPrice({});
  };

  const handleAddMaterial = async () => {
    try {
      const response = await fetch(`${API_URL}/materiales`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newMaterial,
          precio_actualizado: newMaterial.precio_base
        })
      });

      if (response.ok) {
        setOpenDialog(false);
        setNewMaterial({
          codigo: '',
          descripcion: '',
          unidad_id: 1,
          precio_base: '',
          proveedor: ''
        });
        fetchMateriales();
      }
    } catch (err) {
      console.error('Error agregando material:', err);
    }
  };

  const getDisplayPrice = (material) => {
    // Si hay precio personalizado, usarlo
    if (customPrices[material.id]) {
      return customPrices[material.id];
    }
    return material.precio_actualizado;
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Catálogo de Materiales</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Agregar Material
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Base de datos de materiales de construcción para Campeche
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Buscar material por descripción"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Unidad</TableCell>
                <TableCell align="right">Precio Base</TableCell>
                <TableCell align="right">Precio Actual</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell>Última Actualización</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {materiales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {search ? 'No se encontraron materiales con ese criterio' : 'No hay materiales disponibles'}
                  </TableCell>
                </TableRow>
              ) : (
                materiales.map((material) => (
                  <TableRow key={material.id}>
                    <TableCell>{material.codigo}</TableCell>
                    <TableCell>{material.descripcion}</TableCell>
                    <TableCell>{material.unidades?.codigo}</TableCell>
                    <TableCell align="right">{formatPrice(material.precio_base)}</TableCell>
                    <TableCell align="right">
                      {editingId === material.id ? (
                        <TextField
                          type="number"
                          value={editingPrice[material.id] || ''}
                          onChange={(e) => setEditingPrice({ [material.id]: e.target.value })}
                          size="small"
                          sx={{ width: 120 }}
                        />
                      ) : (
                        <Box>
                          {formatPrice(getDisplayPrice(material))}
                          {customPrices[material.id] && (
                            <Chip
                              label="Personalizado"
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{material.proveedor}</TableCell>
                    <TableCell>{new Date(material.fecha_actualizacion).toLocaleDateString('es-MX')}</TableCell>
                    <TableCell align="center">
                      {editingId === material.id ? (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSavePrice(material.id)}
                          >
                            <SaveIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={handleCancelEdit}
                          >
                            <CancelIcon />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleEditPrice(material.id, getDisplayPrice(material))}
                        >
                          <MoneyIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Nuevo Material</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código"
                value={newMaterial.codigo}
                onChange={(e) => setNewMaterial({ ...newMaterial, codigo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={newMaterial.descripcion}
                onChange={(e) => setNewMaterial({ ...newMaterial, descripcion: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Unidad</InputLabel>
                <Select
                  value={newMaterial.unidad_id}
                  onChange={(e) => setNewMaterial({ ...newMaterial, unidad_id: e.target.value })}
                  label="Unidad"
                >
                  {unidades.map((unidad) => (
                    <MenuItem key={unidad.id} value={unidad.id}>
                      {unidad.codigo} - {unidad.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Precio Base"
                type="number"
                value={newMaterial.precio_base}
                onChange={(e) => setNewMaterial({ ...newMaterial, precio_base: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Proveedor"
                value={newMaterial.proveedor}
                onChange={(e) => setNewMaterial({ ...newMaterial, proveedor: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleAddMaterial} variant="contained">
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default CatalogoMateriales;